import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, IconButton, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Content, ContentText } from "./styles";
import DialogActions from "../../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeExpenseReportSTO } from "../../../../../../models/employee-expense-reports/employee-expense-report-sto";
import { ExpenseReportDTO } from "../../../../../../models/expense-reports";
import { EmployeeExpenseReportsApiService } from "../../../../../../services/employee-expense-reports/employee-expense-reports-api.service";
import { toastifyResponse } from "../../../../../../utils/toastify-response/toastify-response";

interface Props {
  expenseReport: ExpenseReportDTO;
  handleNotifyParent: (signal?: AbortSignal) => Promise<void>;
}

interface State {
  isDialogOpen: boolean;
  loading: boolean;
  error: string;
}

const DeleteExpenseReport = ({ expenseReport, handleNotifyParent }: Props) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const [state, setState] = useState<State>({
    isDialogOpen: false,
    loading: false,
    error: "",
  });

  const handleRemoveItem = async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await new EmployeeExpenseReportsApiService().saveItems([
        {
          ...expenseReport,
          isDeleted: true,
        } as EmployeeExpenseReportSTO,
      ]);

      if (!response.error) {
        handleClose();

        handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  };

  const handleClose = () => {
    setState((state) => ({
      ...state,
      isDialogOpen: false,
      loading: false,
      error: "",
    }));
  };

  return (
    <>
      <IconButton
        className="delete_button"
        onClick={() => {
          setState((state) => ({
            ...state,
            isDialogOpen: true,
          }));
        }}
        sx={{
          display: "none",
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -10%)",
          background: "white",
          borderRadius: "25px",
          width: "30px",
          height: "30px",
          boxShadow: theme.shadows[3],

          "&:hover": {
            background: "#E7E7E7",
          },
        }}
      >
        <FontAwesomeIcon
          icon={faTrash}
          color={theme.palette.error.main}
        />
      </IconButton>

      <Dialog
        open={state.isDialogOpen}
        onClose={handleClose}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Delete expense report"}
            handleClose={handleClose}
          />
          <Content>
            <ContentText>{"Are you sure you want to delete this item?"}</ContentText>
          </Content>
          <DialogActions
            handleCancel={handleClose}
            useGoback
            error={state.error}
          >
            <Button
              onClick={handleRemoveItem}
              variant="contained"
              color="error"
            >
              {`${t("buttons.delete")}`}
            </Button>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default DeleteExpenseReport;
